import React, { useEffect, useMemo, useState } from "react";

const DISMISSED_STORAGE_KEY = "dismissedAppAnnouncementIds";

const typeConfig = {
    info: {
        alertClass: "info",
        icon: "fas fa-info-circle",
        label: "Info",
        priority: 2,
    },
    success: {
        alertClass: "success",
        icon: "fas fa-check-circle",
        label: "Success",
        priority: 1,
    },
    warning: {
        alertClass: "warning",
        icon: "fas fa-exclamation-triangle",
        label: "Warning",
        priority: 3,
    },
    error: {
        alertClass: "danger",
        icon: "fas fa-exclamation-circle",
        label: "Critical",
        priority: 4,
    },
};

const getTypeConfig = (type) => typeConfig[type] || typeConfig.info;

const parseDate = (value) => {
    if (!value) {
        return null;
    }

    const normalizedValue = typeof value === "string" ? value.replace(" ", "T") : value;
    const parsed = new Date(normalizedValue);

    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const sortAnnouncements = (items) => {
    return [...items].sort((firstItem, secondItem) => {
        const firstType = getTypeConfig(firstItem.type);
        const secondType = getTypeConfig(secondItem.type);

        if (firstType.priority !== secondType.priority) {
            return secondType.priority - firstType.priority;
        }

        const firstDate = parseDate(firstItem.starts_at) || parseDate(firstItem.created);
        const secondDate = parseDate(secondItem.starts_at) || parseDate(secondItem.created);

        return (secondDate?.getTime() || 0) - (firstDate?.getTime() || 0);
    });
};

const formatDateLabel = (value) => {
    const parsed = parseDate(value);

    if (!parsed) {
        return null;
    }

    return parsed.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const getAnnouncementId = (announcement) => String(announcement?.id || "");

const linkPattern = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
const trailingPunctuationPattern = /[.,!?;:)]+$/;

const getSafeLink = (value) => {
    const href = value.startsWith("www.") ? `https://${value}` : value;

    try {
        const url = new URL(href);
        return ["http:", "https:"].includes(url.protocol) ? url.href : null;
    } catch {
        return null;
    }
};

export function AnnouncementMessage({ message, className = "" }) {
    const text = String(message || "");

    if (!text) {
        return null;
    }

    const lines = text.split(/\\n|\r?\n/);

    return (
        <>
            {lines.map((line, lineIndex) => {
                const parts = line.split(linkPattern);

                return (
                    <React.Fragment key={`${line}-${lineIndex}`}>
                        {lineIndex > 0 && <br />}
                        {parts.map((part, partIndex) => {
                            if (!part.match(linkPattern)) {
                                return part;
                            }

                            const trailingPunctuation = part.match(trailingPunctuationPattern)?.[0] || "";
                            const linkText = trailingPunctuation ? part.slice(0, -trailingPunctuation.length) : part;
                            const href = getSafeLink(linkText);

                            if (!href) {
                                return part;
                            }

                            return (
                                <React.Fragment key={`${linkText}-${lineIndex}-${partIndex}`}>
                                    <a
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={className || undefined}
                                    >
                                        {linkText}
                                    </a>
                                    {trailingPunctuation}
                                </React.Fragment>
                            );
                        })}
                    </React.Fragment>
                );
            })}
        </>
    );
}

const useAnnouncementState = (announcements = []) => {
    const [dismissedIds, setDismissedIds] = useState([]);

    useEffect(() => {
        try {
            const storedIds = JSON.parse(sessionStorage.getItem(DISMISSED_STORAGE_KEY) || "[]");
            setDismissedIds(Array.isArray(storedIds) ? storedIds.map(String) : []);
        } catch {
            setDismissedIds([]);
        }
    }, []);

    const sortedAnnouncements = useMemo(
        () => sortAnnouncements(Array.isArray(announcements) ? announcements : []),
        [announcements]
    );

    const visibleBannerAnnouncement = useMemo(() => {
        return sortedAnnouncements.find((announcement) => !dismissedIds.includes(getAnnouncementId(announcement))) || null;
    }, [dismissedIds, sortedAnnouncements]);

    const dismissAnnouncement = (announcementId) => {
        const normalizedId = String(announcementId);
        const nextDismissedIds = Array.from(new Set([...dismissedIds, normalizedId]));

        setDismissedIds(nextDismissedIds);
        sessionStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(nextDismissedIds));
    };

    return {
        sortedAnnouncements,
        visibleBannerAnnouncement,
        dismissAnnouncement,
    };
};

export function AppAnnouncementsMenu({ announcements = [], isLoading = false }) {
    const { sortedAnnouncements } = useAnnouncementState(announcements);

    return (
        <li className="nav-item dropdown notification_dropdown">
            <button
                className="nav-link bell-link ai-icon position-relative"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                aria-label="View active announcements"
            >
                <i className="fas fa-bell"></i>
                {sortedAnnouncements.length > 0 && (
                    <span
                        className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                        style={{ fontSize: "0.65rem" }}
                    >
                        {sortedAnnouncements.length}
                    </span>
                )}
            </button>
            <div
                className="dropdown-menu dropdown-menu-end p-0"
                style={{ width: "360px", maxWidth: "calc(100vw - 2rem)" }}
            >
                <div className="p-3 border-bottom d-flex align-items-center justify-content-between">
                    <strong>Announcements</strong>
                    <span className="badge bg-light text-dark">{sortedAnnouncements.length}</span>
                </div>

                <div style={{ maxHeight: "420px", overflowY: "auto" }}>
                    {isLoading && (
                        <div className="p-3 text-muted small">Loading active announcements...</div>
                    )}

                    {!isLoading && sortedAnnouncements.length === 0 && (
                        <div className="p-3 text-muted small">There are no active announcements right now.</div>
                    )}

                    {!isLoading && sortedAnnouncements.map((announcement) => {
                        const config = getTypeConfig(announcement.type);
                        const startsAt = formatDateLabel(announcement.starts_at);
                        const endsAt = formatDateLabel(announcement.ends_at);

                        return (
                            <div key={getAnnouncementId(announcement)} className="p-3 border-bottom">
                                <div className="d-flex align-items-start gap-2">
                                    <i className={`${config.icon} text-${config.alertClass} mt-1`}></i>
                                    <div className="flex-grow-1">
                                        <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                                            <strong className="small text-dark">{announcement.title || config.label}</strong>
                                            <span className={`badge bg-${config.alertClass}-subtle text-${config.alertClass}`}>
                                                {config.label}
                                            </span>
                                        </div>
                                        <div className="small text-muted">
                                            <AnnouncementMessage message={announcement.message} className="text-reset text-decoration-underline" />
                                        </div>
                                        {(startsAt || endsAt) && (
                                            <div className="small text-muted mt-2">
                                                {startsAt && <span>Starts: {startsAt}</span>}
                                                {startsAt && endsAt && <span className="mx-2">|</span>}
                                                {endsAt && <span>Ends: {endsAt}</span>}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </li>
    );
}

export default function AppAnnouncementsBanner({ announcements = [] }) {
    const { visibleBannerAnnouncement, dismissAnnouncement } = useAnnouncementState(announcements);

    return (
        visibleBannerAnnouncement ? (
            <div className={`alert alert-${getTypeConfig(visibleBannerAnnouncement.type).alertClass} alert-alt solid alert-dismissible fade show mb-0 rounded-0 border-0`}>
                <div className="d-flex align-items-start justify-content-between gap-3 px-3 py-2">
                    <div className="d-flex align-items-start gap-3">
                        <i className={`${getTypeConfig(visibleBannerAnnouncement.type).icon} mt-1`}></i>
                        <div>
                            <div className="fw-semibold">{visibleBannerAnnouncement.title}</div>
                            <div>
                                <AnnouncementMessage message={visibleBannerAnnouncement.message} className="text-reset text-decoration-underline" />
                            </div>
                            {(formatDateLabel(visibleBannerAnnouncement.starts_at) || formatDateLabel(visibleBannerAnnouncement.ends_at)) && (
                                <div className="small mt-1 opacity-75">
                                    {formatDateLabel(visibleBannerAnnouncement.starts_at) && (
                                        <span>Starts: {formatDateLabel(visibleBannerAnnouncement.starts_at)}</span>
                                    )}
                                    {formatDateLabel(visibleBannerAnnouncement.starts_at) && formatDateLabel(visibleBannerAnnouncement.ends_at) && (
                                        <span className="mx-2">|</span>
                                    )}
                                    {formatDateLabel(visibleBannerAnnouncement.ends_at) && (
                                        <span>Ends: {formatDateLabel(visibleBannerAnnouncement.ends_at)}</span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        className="btn-close btn-close-white"
                        aria-label="Dismiss announcement"
                        onClick={() => dismissAnnouncement(getAnnouncementId(visibleBannerAnnouncement))}
                    ></button>
                </div>
            </div>
        ) : null
    );
}
